import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateSavedViewDto,
  SavedViewDto,
  SavedViewScreen,
} from "@gw/shared-types";
import {
  createSavedView,
  deleteSavedView,
  listSavedViews,
} from "@/api/endpoints";

export function useSavedViews(screen: SavedViewScreen) {
  return useQuery<SavedViewDto[]>({
    queryKey: ["savedViews", screen],
    queryFn: () => listSavedViews(screen),
    staleTime: 60_000,
    // Saved views are optional infra. If the API isn't configured (503) or otherwise fails,
    // the screen still works without them — surface the error in the UI when relevant.
    retry: false,
  });
}

export function useCreateSavedView(screen: SavedViewScreen) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateSavedViewDto) => createSavedView(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["savedViews", screen] });
    },
  });
}

export function useDeleteSavedView(screen: SavedViewScreen) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSavedView(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["savedViews", screen] });
    },
  });
}
